import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Container, Row, Col, Button } from "react-bootstrap";
import * as Yup from "yup";
import "./DeliveryForm.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import GeneralApiService from "../../services/generalServices";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import { useDispatch, useSelector } from "react-redux";
import InventoryServices from "../../services/InventoryServices";
import translations from "../../assets/locals/translations";

const paymentList = [
  { value: "Cash", label: "Cash" },
  { value: "Loan", label: "Loan" },
];

const cashOptions = [
  { label: "Rokda (In-Hand)", value: "rokda" },
  { label: "Check", value: "check" },
];

const DeliveryForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 1. Get Language
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];

  const InventoryService = new InventoryServices();
  const { ev_stage, editData, stockData = null } = location?.state ?? {};

  const [inventoryItem, setInventoryItem] = useState({
    rawData: [],
    chassisList: [],
    engineList: [],
  });
  const [banklist, setBankList] = useState([]);
  const [modelslistByAgencyMake, setModelslistByAgencyMake] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [filterModelList, setFilterModelList] = useState([]);
  const [customerData, setCustomerData] = useState({});
  const [makeList, setMakeList] = useState([]);
  const [dspList, setDspList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);
  const [filterdTalukaList, setfilterdTalukaList] = useState([]);
  const [villageList, setVillageList] = useState([]);
  const [filteredVillageList, setFileredVillageList] = useState([]);
  const [enquirySourceList, setEnqirySourceList] = useState([]);
  const [loading, setLoading] = useState(true);

  // 2. Define Dynamic Schema using useMemo to update when language changes
  const DeliveryFormSchema = useMemo(() => {
    return Yup.object().shape({
      customerName: Yup.string().required(t.required || "Customer name is required"),
      enquiredDate: Yup.date().required(t.required || "Enquired date is required"),
      village: Yup.string().required(t.required || "Village is required"),
      taluka: Yup.string().required(t.required || "Taluka is required"),
      district: Yup.string().required(t.required || "District is required"),
      customerMobile: Yup.string().required(t.required || "Customer mobile number is required"),
      whatsappNumber: Yup.string().required(t.required || "WhatsApp number is required"),
      deliveryDate: Yup.date().required(t.required || "Delivery date is required"),
      enquirySource: Yup.string().required(t.required || "Enquiry Source is required"),
      createdby: Yup.string().required(t.required || "Created By is required"),
      deliverySalesman: Yup.string().required(t.required || "Delivery Salesman is required"),
      salesPerson: Yup.string().required(t.required || "Sales person is required"),
      modelName: Yup.string().required(t.required || "Model name is required"),
      chasisNumber: Yup.string().required(t.required || "Chasis number is required"),
      engineNumber: Yup.string().required(t.required || "Engine number is required"),
      paymentMethod: Yup.string().required(t.required || "Payment method is required"),
      paymentDetail: Yup.string(),
      isOldOwned: Yup.boolean(),
      oldmake: Yup.string().when("isOldOwned", {
        is: true,
        then: (schema) => schema.required(t.required || "Old Make is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      oldmodel: Yup.string().when("isOldOwned", {
        is: true,
        then: (schema) => schema.required(t.required || "Old Model is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      oldyear: Yup.string().when("isOldOwned", {
        is: true,
        then: (schema) => schema.required(t.required || "Old Year is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      newPrice: Yup.string().required(t.required || "New Price is required"),
      oldPrice: Yup.string(),
      remainingPrice: Yup.string(),
      retailDate: Yup.date().required(t.required || "Retail date is required"),
    });
  }, [t]);

  const [initialValues, setInitialValues] = useState({
    customerName: "",
    enquiredDate: null,
    village: "",
    taluka: "",
    district: "",
    customerMobile: "",
    whatsappNumber: "",
    deliveryDate: null,
    enquirySource: "",
    createdby: "",
    deliverySalesman: "",
    salesPerson: "",
    modelName: "",
    chasisNumber: "",
    engineNumber: "",
    paymentMethod: "",
    paymentDetail: "",
    isOldOwned: false,
    oldmake: "",
    oldmodel: "",
    oldyear: "",
    newPrice: "",
    oldPrice: "",
    remainingPrice: "",
    retailDate: null,
  });

  const getModelStockData = async (model) => {
    try {
      const response = await InventoryService.getModelStockData(model);
      if (response && response.isSuccess) {
        const rawData = response.result;

        const chassisList = rawData.map((item) => ({
          label: item.chassis,
          value: item.chassis,
        }));

        const engineList = rawData.map((item) => ({
          label: item.engine,
          value: item.engine,
        }));

        // Include editData.chassis_no if not already in the list
        if (
          editData?.chassis_no &&
          !chassisList.find((item) => item.value === editData.chassis_no)
        ) {
          chassisList.push({
            label: editData.chassis_no,
            value: editData.chassis_no,
          });
        }

        // Include editData.engine_no if not already in the list
        if (
          editData?.engine_no &&
          !engineList.find((item) => item.value === editData.engine_no)
        ) {
          engineList.push({
            label: editData.engine_no,
            value: editData.engine_no,
          });
        }

        setInventoryItem({
          rawData,
          chassisList,
          engineList,
        });
      }
    } catch (error) {
      console.error(error, "Error getting Model Stock data");
    }
  };

  const fetchBankList = async () => {
    try {
      const url = `${process.env.REACT_APP_NODE_URL}/api/get-bank-list`;
      const config = {
        headers: {
          token: localStorage.getItem("rbacToken"),
        },
      };

      const response = await axios.get(url, config);

      if (response.data?.isSuccess) {
        setBankList(
          response.data.result.map((item) => ({
            value: item.bankname,
            label: item.bankname,
          }))
        );
      } else {
        console.error("API request was not successful");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const url = `${process.env.REACT_APP_NODE_URL}/api/general/get-customerData/${customerId}`;
      const config = {
        headers: {
          token: localStorage.getItem("rbacToken"),
        },
      };

      const response = await axios.get(url, config);

      if (response.data?.isSuccess) {
        const [rowData] = response.data.result.customerData;
        setCustomerData(rowData);
      } else {
        console.error("API request was not successful");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getVehicleMakeModels = async () => {
    const url = `${process.env.REACT_APP_NODE_URL}/api/master/get-make-modal-list`;
    const config = {
      headers: {
        token: localStorage.getItem("rbacToken"),
      },
    };

    try {
      const response = await axios.get(url, config);
      if (response && response.data) {
        const {
          makeList,
          modelList,
          modelslistByAgencyMake,
          dspList,
          enquirySourceList,
          districtList,
          talukaList,
          villageList,
        } = response.data.result;
        setMakeList(
          makeList.map((item) => ({ value: item.id, label: item.name }))
        );
        setModelList(modelList);
        setModelslistByAgencyMake(
          modelslistByAgencyMake.map((item) => ({
            value: item.id,
            label: item.modalName,
          }))
        );
        setDspList(dspList);
        setEnqirySourceList(enquirySourceList);
        setDistrictList(districtList);
        setTalukaList(talukaList);
        setVillageList(villageList);
      } else {
        setMakeList([]);
        setModelList([]);
        setModelslistByAgencyMake([]);
        setDspList([]);
        setEnqirySourceList([]);
        setDistrictList([]);
        setTalukaList([]);
        setVillageList([]);
      }
    } catch (error) {
      console.error("Error fetching getVehicleMakeModels:", error);
    } finally {
      setLoading(false); // Set loading to false after fetching
    }
  };
  useEffect(() => {
    if (editData && editData.id) {
      fetchCustomerDetails(editData.id);
    }
  }, [editData]);
  useEffect(() => {
    getVehicleMakeModels();
    fetchBankList();
  }, []);

  useEffect(() => {
    console.log(editData, customerData, "---------");
    if (!loading && editData && customerData) {
      setInitialValues({
        customerName: editData.first_name,
        enquiredDate: editData.enquiry_date
          ? moment(editData.enquiry_date).format("YYYY-MM-DD")
          : "",
        village: customerData.village,
        taluka: customerData.taluka,
        district: customerData.district,
        customerMobile: editData.phone_number,
        whatsappNumber: editData.whatsapp_number,
        deliveryDate: editData.delivery_date
          ? moment(editData.delivery_date).format("YYYY-MM-DD")
          : "",
        enquirySource: customerData.enquiry_source_id,
        createdby: customerData.user_created,
        deliverySalesman: customerData.delivery_salesman,
        salesPerson: customerData.salesperson_id,
        modelName: customerData.modal_id,
        chasisNumber: editData.chassis_no,
        engineNumber: editData.engine_no,
        paymentMethod: editData.payment_mode,
        paymentDetail: editData.payment_details ?? "",
        isOldOwned: customerData.old_tractor === "Yes",
        oldmake: customerData.oldmake,
        oldmodel: customerData.oldmodel,
        oldyear: customerData.oldyear,
        newPrice: (editData.new_tractor_price ?? 0).toString(),
        oldPrice: (editData.old_tractor_price ?? 0).toString(),
        remainingPrice: (editData.remaining_price ?? 0).toString(),
        retailDate: customerData.retail_date
          ? moment(customerData.retail_date).format("YYYY-MM-DD")
          : "",
      });

      setfilterdTalukaList(
        talukaList.filter((val) => val.district_id == customerData.district)
      );
      setFileredVillageList(
        villageList.filter((val) => val.taluka_id == customerData.taluka)
      );
      const filtered = modelList
        .filter((model) => model.manufacturerId == customerData.oldmake)
        .map((val) => ({ value: val.id, label: val.modalName }));

      setFilterModelList(filtered);
      if (customerData.modal_id) {
        getModelStockData(customerData.modal_id);
      }
    }
  }, [loading, editData, customerData]);

  useEffect(() => {
    if (!editData && stockData && stockData.model) {
      const filtered = modelList
        .filter((model) => model.id == stockData.model)
        .map((val) => ({ value: val.id, label: val.modalName }));

      setFilterModelList(filtered);
      setInitialValues((preVal) => ({
        ...preVal,
        modelName: stockData.model,
        chasisNumber: stockData.chassis,
        engineNumber: stockData.engine,
      }));
      if (stockData.model) {
        getModelStockData(stockData.model);
      }
    }
  }, [stockData]);

  const formatRupee = (value) => {
    // Ensure the value has a rupee symbol and only numeric characters after it
    return value.replace(/[^\d]/g, ""); // Keep only numbers
  };

  const years = Array.from({ length: 2024 - 1970 + 1 }, (_, i) => {
    const year = (1970 + i).toString();
    return { value: year, label: year };
  });

  const formatType = (type) =>
    type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

  const handleSubmit = async (values) => {
    const processPrice = (price) => parseInt(price.replace(/\s/g, "")) || 0;

    const processedValues = {
      ...values,
      newPrice: processPrice(values.newPrice),
      oldPrice: processPrice(values.oldPrice),
      remainingPrice: processPrice(values.remainingPrice),
    };

    try {
      // Case when stockData
      if (stockData && Object.keys(stockData).length > 0) {
        const response = await InventoryService.registerNewDelivery(
          processedValues
        );
        if (response?.success) {
          dispatch(setShowMessage(response.message));
          navigate("/stocks");
        }
        return; // Exit
      }

      // Case when editing enquiry stage
      processedValues["editType"] = ev_stage.type;
      processedValues["customerId"] = editData.id;
      console.log("Processed Form Data:", processedValues);

      const response = await new GeneralApiService().editEnquiryStageRequest(
        processedValues
      );
      if (response?.success) {
        const formattedType = formatType(ev_stage.type);
        navigate(`/sale/data/${formattedType}`, { state: { stage: ev_stage } });
        dispatch(setShowMessage(response.message));
      } else {
        console.error("Failed to edit enquiry stage");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Container className="bg-white rounded">
      <Row className="justify-content-md-center">
        <Col md={12}>
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={DeliveryFormSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values, touched, errors, setFieldValue }) => {
              const calculateRemainingPrice = () => {
                const newPrice =
                  parseInt(values.newPrice.replace(/[^\d]/g, ""), 10) || 0;
                const oldPrice =
                  parseInt(values.oldPrice.replace(/[^\d]/g, ""), 10) || 0;
                const remainingPrice = Math.max(newPrice - oldPrice, 0);
                setFieldValue(
                  "remainingPrice",
                  formatRupee(remainingPrice.toString())
                );
              };
              const onDistrictChange = (e) => {
                const selectedDistrict = e.target.value;
                setFieldValue("district", selectedDistrict);
                setfilterdTalukaList(
                  talukaList.filter(
                    (val) => val.district_id == selectedDistrict
                  )
                );
              };
              const onTalukaChange = (e) => {
                const selectedTaluka = e.target.value;
                setFieldValue("taluka", selectedTaluka);
                setFileredVillageList(
                  villageList.filter((val) => val.taluka_id == selectedTaluka)
                );
              };
              const onMakeChange = (e) => {
                const selectedMake = e.target.value;
                setFieldValue("oldmake", selectedMake);
                const filtered = modelList
                  .filter((model) => model.manufacturerId == selectedMake)
                  .map((val) => ({ value: val.id, label: val.modalName }));

                setFilterModelList(filtered);
              };
              const onPaymentMethodChange = (e) => {
                const mode = e.target.value;
                setFieldValue("paymentMethod", mode);
              };
              const onModelChange = (e) => {
                const selectedModel = e.target.value;
                setFieldValue("modelName", selectedModel);
                getModelStockData(selectedModel);
              };
              const onChassisChange = (e, setFieldValue) => {
                const selectedChassis = e.target.value;
                setFieldValue("chasisNumber", selectedChassis);

                // Find matching engine from rawData
                const matchedItem = inventoryItem.rawData.find(
                  (item) => item.chassis === selectedChassis
                );

                if (matchedItem) {
                  setFieldValue("engineNumber", matchedItem.engine);
                } else {
                  setFieldValue("engineNumber", ""); // Clear if not found
                }
              };

              return (
                <Form>
                  <Row className="mb-1">
                    <Col md={12}>
                      <div className="d-flex align-items-center justify-content-between">
                        <label className="m-0 form-label">
                          {ev_stage && ev_stage.type
                            ? (t[ev_stage.type] || ev_stage.type.charAt(0).toUpperCase() + ev_stage.type.slice(1))
                            : (t.delivery || "DELIVERY")}
                        </label>

                        <Button
                          variant="outline-secondary"
                          className="text-muted mt-1"
                          size="small"
                          onClick={() => {
                            navigate(-1);
                          }}
                        >
                          {t.back || "BACK"}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <label htmlFor="customerName" className="form-label">
                        {t.name || "Name"}
                      </label>
                      <Field
                        name="customerName"
                        className="form-control"
                        type="text"
                      />
                      <ErrorMessage
                        name="customerName"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                    <Col md={4}>
                      <label htmlFor="customerMobile" className="form-label">
                        {t.phoneNumber || "Phone Number"}
                      </label>
                      <Field
                        name="customerMobile"
                        className="form-control"
                        type="phone"
                      />
                      <ErrorMessage
                        name="customerMobile"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                    <Col md={4}>
                      <label htmlFor="whatsappNumber" className="form-label">
                        {t.whatsappnumber || "WhatsApp Number"}
                      </label>
                      <Field
                        name="whatsappNumber"
                        className="form-control"
                        type="phone"
                      />
                      <ErrorMessage
                        name="whatsappNumber"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <label htmlFor="district" className="form-label">
                        {t.district || "District"}
                      </label>
                      <Field
                        as="select"
                        name="district"
                        className="form-control"
                        onChange={(e) => onDistrictChange(e)}
                      >
                        <option value="" label={t.selectDistrict || "Select District"} />
                        {districtList.map((dist) => (
                          <option
                            key={dist.value}
                            value={dist.value}
                            label={dist.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="district"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>

                    <Col md={4}>
                      <label htmlFor="taluka" className="form-label">
                        {t.taluka || "Taluka"}
                      </label>
                      <Field
                        as="select"
                        name="taluka"
                        className="form-control"
                        onChange={(e) => onTalukaChange(e)}
                      >
                        <option value="" label={t.selectTaluka || "Select Taluka"} />
                        {filterdTalukaList.map((taluka) => (
                          <option
                            key={taluka.value}
                            value={taluka.value}
                            label={taluka.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="taluka"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>

                    <Col md={4}>
                      <label htmlFor="village" className="form-label">
                        {t.village || "Village"}
                      </label>
                      <Field
                        as="select"
                        name="village"
                        className="form-control"
                      >
                        <option value="" label={t.selectVillage || "Select Village"} />
                        {filteredVillageList.map((village) => (
                          <option
                            key={village.value}
                            value={village.value}
                            label={village.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="village"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <label htmlFor="enquiredDate" className="form-label">
                        {t.enqdate || "Enquiry Date"}
                      </label>
                      <Field
                        name="enquiredDate"
                        className="form-control"
                        type="date"
                      />
                      <ErrorMessage
                        name="enquiredDate"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>

                    <Col md={4}>
                      <label htmlFor="deliveryDate" className="form-label">
                        {t.expdeliveryd || "Expected Delivery Date"}
                      </label>
                      <Field
                        name="deliveryDate"
                        className="form-control"
                        type="date"
                      />
                      <ErrorMessage
                        name="deliveryDate"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>

                    <Col md={4}>
                      <label htmlFor="enquirySource" className="form-label">
                        {t.enquirysource || "Enquiry Source"}
                      </label>
                      <Field
                        as="select"
                        name="enquirySource"
                        className="form-control"
                      >
                        <option value="" label={t.selectEnquirySource || "Select Enquiry Source"} />
                        {enquirySourceList.map((eos) => (
                          <option
                            key={eos.value}
                            value={eos.value}
                            label={eos.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="enquirySource"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <label htmlFor="deliverySalesman" className="form-label">
                        {t.deliverysalesman || "Delivery Salesman"}
                      </label>
                      <Field
                        as="select"
                        name="deliverySalesman"
                        className="form-control"
                      >
                        <option value="" label={t.selectDeliverySalesman || "Select Delivery Salesman"} />
                        {dspList.map((dsp) => (
                          <option
                            key={dsp.value}
                            value={dsp.value}
                            label={dsp.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="deliverySalesman"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>

                    <Col md={4}>
                      <label htmlFor="salesPerson" className="form-label">
                        {t.salesp || "Sales Person"}
                      </label>
                      <Field
                        as="select"
                        name="salesPerson"
                        className="form-control"
                      >
                        <option value="" label={t.selectSalesPerson || "Select Sales Person"} />
                        {dspList.map((dsp) => (
                          <option
                            key={dsp.value}
                            value={dsp.value}
                            label={dsp.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="salesPerson"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                    <Col md={4}>
                      <label htmlFor="createdby" className="form-label">
                        {t.enquiryad || "Enquiry Ad"}
                      </label>
                      <Field
                        as="select"
                        name="createdby"
                        className="form-control"
                      >
                        <option value="" label={t.selectEnquiryAd || "Select Enquiry Ad"} />
                        {dspList.map((dsp) => (
                          <option
                            key={dsp.value}
                            value={dsp.value}
                            label={dsp.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="createdby"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <label htmlFor="modelName" className="form-label">
                        {t.modelname || "Model Name"}
                      </label>
                      <Field
                        as="select"
                        name="modelName"
                        className="form-control"
                        onChange={(e) => onModelChange(e)}
                      >
                        <option value="" label={t.selectModel || "Select Model"} />
                        {modelslistByAgencyMake.map((model) => (
                          <option
                            key={model.value}
                            value={model.value}
                            label={model.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="modelName"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>

                    <Col md={4}>
                      <label htmlFor="chasisNumber" className="form-label">
                        {t.chassisNumber || "Chassis Number"}
                      </label>
                      <Field
                        name="chasisNumber"
                        as="select"
                        className="form-control"
                        onChange={(e) => onChassisChange(e, setFieldValue)}
                      >
                        <option value="">{t.selectChassis || "Select Chassis"}</option>
                        {inventoryItem.chassisList?.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="chasisNumber"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>

                    <Col md={4}>
                      <label htmlFor="engineNumber" className="form-label">
                        {t.engineNumber || "Engine Number"}
                      </label>
                      <Field
                        name="engineNumber"
                        as="select"
                        className="form-control"
                      >
                        <option value="">{t.selectEngine || "Select Engine"}</option>
                        {inventoryItem.engineList?.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="engineNumber"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                  {/* Checkbox for Is Old Owned */}
                  <Row className="mb-2">
                    <Col md={12}>
                      <label className="form-check-label">
                        <Field
                          type="checkbox"
                          name="isOldOwned"
                          className="form-check-input"
                        />{" "}
                        {t.isOldOwned || "Is Old Owned?"}
                      </label>
                    </Col>
                  </Row>
                  {values.isOldOwned && (
                    <Row className="mb-3">
                      <Col md={4}>
                        <label htmlFor="oldmake" className="form-label">
                          {t.make || "Make"}
                        </label>
                        <Field
                          as="select"
                          name="oldmake"
                          className="form-control"
                          onChange={(e) => onMakeChange(e)}
                        >
                          <option value="" label={t.selectMake || "Select Make"} />
                          {makeList.map((make) => (
                            <option
                              key={make.value}
                              value={make.value}
                              label={make.label}
                            />
                          ))}
                        </Field>
                        <ErrorMessage
                          name="oldmake"
                          component={({ children }) => (
                            <span
                              style={{ color: "darkred", fontSize: "0.875rem" }}
                            >
                              {children}
                            </span>
                          )}
                        />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="oldmodel" className="form-label">
                          {t.modelname || "Model"}
                        </label>
                        <Field
                          as="select"
                          name="oldmodel"
                          className="form-control"
                        >
                          <option value="" label={t.selectModel || "Select Model"} />
                          {filterModelList.map((model) => (
                            <option
                              key={model.value}
                              value={model.value}
                              label={model.label}
                            />
                          ))}
                        </Field>
                        <ErrorMessage
                          name="oldmodel"
                          component={({ children }) => (
                            <span
                              style={{ color: "darkred", fontSize: "0.875rem" }}
                            >
                              {children}
                            </span>
                          )}
                        />
                      </Col>
                      <Col md={4}>
                        <label htmlFor="oldyear" className="form-label">
                          {t.year || "Year"}
                        </label>
                        <Field
                          as="select"
                          name="oldyear"
                          className="form-control"
                        >
                          <option value="" label={t.selectYear || "Select Year"} />
                          {years.map((year) => (
                            <option
                              key={year.value}
                              value={year.value}
                              label={year.label}
                            />
                          ))}
                        </Field>
                        <ErrorMessage
                          name="oldyear"
                          component={({ children }) => (
                            <span
                              style={{ color: "darkred", fontSize: "0.875rem" }}
                            >
                              {children}
                            </span>
                          )}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col md={4}>
                      <label htmlFor="paymentMethod" className="form-label">
                        {t.paymentMethod || "Payment Method"}
                      </label>
                      <Field
                        as="select"
                        name="paymentMethod"
                        className="form-control"
                        onChange={(e) => onPaymentMethodChange(e)}
                      >
                        <option value="" label={t.selectPaymentMethod || "Select Payment Method"} />
                        {paymentList.map((payment) => (
                          <option
                            key={payment.value}
                            value={payment.value}
                            label={payment.label}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="paymentMethod"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                    <Col md={4}>
                      <label htmlFor="paymentDetail" className="form-label">
                        {t.paymentDetails || "Payment Details"}
                      </label>
                      <Field
                        as="select"
                        name="paymentDetail"
                        className="form-control"
                      >
                        <option
                          value=""
                          label={
                            values.paymentMethod === "Cash"
                              ? (t.selectPaymentDetails || "Select Payment Details")
                              : (t.selectBankName || "Select Bank Name")
                          }
                        />
                        {(values.paymentMethod === "Cash"
                          ? cashOptions
                          : banklist
                        ).map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="paymentDetail"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                    <Col md={4}>
                      <label htmlFor="retailDate" className="form-label">
                        {t.retailDate || "Retail Date"}
                      </label>
                      <Field
                        name="retailDate"
                        className="form-control"
                        type="date"
                      />
                      <ErrorMessage
                        name="retailDate"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <label htmlFor="newPrice" className="form-label">
                        {t.newtractorprice || "New Tractor Price"}
                      </label>
                      <Field
                        name="newPrice"
                        className="form-control"
                        type="text"
                        onBlur={() => {
                          calculateRemainingPrice();
                        }}
                        placeholder="0"
                      />
                      <ErrorMessage
                        name="newPrice"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                    <Col md={4}>
                      <label htmlFor="oldPrice" className="form-label">
                        {t.oldtractorprice || "Old Tractor Price"}
                      </label>
                      <Field
                        name="oldPrice"
                        className="form-control"
                        type="text"
                        onBlur={() => {
                          calculateRemainingPrice();
                        }}
                        placeholder="0"
                      />
                      <ErrorMessage
                        name="oldPrice"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                    <Col md={4}>
                      <label htmlFor="remainingPrice" className="form-label">
                        {t.remainingPrice || "Remaining Price"}
                      </label>
                      <Field
                        name="remainingPrice"
                        className="form-control"
                        type="text"
                        placeholder="0"
                        readOnly
                      />
                      <ErrorMessage
                        name="remainingPrice"
                        component={({ children }) => (
                          <span
                            style={{ color: "darkred", fontSize: "0.875rem" }}
                          >
                            {children}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>

                  {/* Save Changes */}
                  <Row className="mb-2">
                    <Col md={12}>
                      <Button
                        variant="outline-primary"
                        className="px-5"
                        type="submit"
                      // disabled={isSubmitting}
                      >
                        {t.saveChanges || "Save Changes"}
                      </Button>

                      <Button
                        variant="outline-primary"
                        className=" ms-2 px-4"
                        onClick={() => {
                          navigate(-1);
                        }}
                      >
                        {t.cancel || "Cancel"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};

export default DeliveryForm;